import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Image,
  Input,
  Radio,
  RadioGroup,
  Spinner,
  Text,
  useColorModeValue,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { AttachmentIcon } from "@chakra-ui/icons";
import {
  deleteDigitalSignature,
  saveDigitalSignatureUrl,
  uploadDigitalSignature,
} from "../services/userService";
import {
  isSignatureFileSizeValid,
  isSupportedSignatureFile,
  isValidSignatureUrl,
} from "../utils/digitalSignature";

const getErrorMessage = (error) =>
  error?.response?.data?.message || error?.message || "Signature update failed";

const UserDigitalSignatureManager = ({
  userId,
  digitalSignature,
  onChange,
  emptyMessage = "No digital signature saved.",
  unavailableMessage = "Create a login account for this employee before adding a digital signature.",
}) => {
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const mutedColor = useColorModeValue("gray.600", "gray.400");
  const previewBg = useColorModeValue("gray.50", "gray.700");
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef();

  const [mode, setMode] = useState(digitalSignature?.type || "upload");
  const [url, setUrl] = useState(
    digitalSignature?.type === "url" ? digitalSignature.url : "",
  );
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(digitalSignature?.url || "");
  const [error, setError] = useState("");
  const [previewError, setPreviewError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    setMode(digitalSignature?.type || "upload");
    setUrl(digitalSignature?.type === "url" ? digitalSignature.url : "");
    setFile(null);
    setPreview(digitalSignature?.url || "");
    setError("");
    setPreviewError(false);
  }, [digitalSignature, userId]);

  if (!userId) {
    return (
      <Alert status="info" borderRadius="md">
        <AlertIcon />
        <AlertDescription>{unavailableMessage}</AlertDescription>
      </Alert>
    );
  }

  const handleFileChange = (event) => {
    const selectedFile = event.target.files?.[0];
    setError("");
    setPreviewError(false);

    if (!selectedFile) {
      setFile(null);
      setPreview(digitalSignature?.url || "");
      return;
    }

    if (!isSupportedSignatureFile(selectedFile)) {
      setError("Use a PNG, JPEG, or WebP image.");
      setFile(null);
      return;
    }

    if (!isSignatureFileSizeValid(selectedFile)) {
      setError("Signature image must be 5MB or smaller.");
      setFile(null);
      return;
    }

    setFile(selectedFile);
    setPreview(URL.createObjectURL(selectedFile));
  };

  const handleUrlChange = (event) => {
    const value = event.target.value;
    setUrl(value);
    setError("");
    setPreviewError(false);
    setPreview(isValidSignatureUrl(value) ? value.trim() : "");
  };

  const handleModeChange = (nextMode) => {
    setMode(nextMode);
    setError("");
    setPreviewError(false);
    setPreview(
      nextMode === "url" && isValidSignatureUrl(url)
        ? url.trim()
        : digitalSignature?.url || "",
    );
  };

  const handleSave = async () => {
    setError("");
    setIsSaving(true);

    try {
      const response =
        mode === "upload"
          ? await uploadDigitalSignature(userId, file)
          : await saveDigitalSignatureUrl(userId, url);

      onChange?.(response.data);
      toast({
        title: "Digital signature saved",
        status: "success",
        duration: 2500,
        isClosable: true,
      });
    } catch (saveError) {
      const message = getErrorMessage(saveError);
      setError(message);
      toast({
        title: "Could not save signature",
        description: message,
        status: "error",
        duration: 3500,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);

    try {
      await deleteDigitalSignature(userId);
      onChange?.(null);
      setUrl("");
      onClose();
      toast({
        title: "Digital signature removed",
        status: "success",
        duration: 2500,
        isClosable: true,
      });
    } catch (deleteError) {
      toast({
        title: "Could not remove signature",
        description: getErrorMessage(deleteError),
        status: "error",
        duration: 3500,
        isClosable: true,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const hasSignature = Boolean(digitalSignature?.url);
  const canSave = mode === "upload" ? Boolean(file) : isValidSignatureUrl(url);

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={4}>
        <Text fontWeight="semibold">Digital Signature</Text>
        {hasSignature && (
          <Badge colorScheme="green" textTransform="capitalize">
            {digitalSignature.type}
          </Badge>
        )}
      </Flex>

      <Flex
        align="center"
        justify="center"
        bg={previewBg}
        border="1px dashed"
        borderColor={borderColor}
        borderRadius="md"
        minH="150px"
        mb={4}
        p={4}
      >
        {preview && !previewError ? (
          <Image
            src={preview}
            alt="Digital signature preview"
            maxW="320px"
            maxH="140px"
            objectFit="contain"
            onError={() => setPreviewError(true)}
          />
        ) : (
          <Text color={mutedColor} fontSize="sm">
            {previewError ? "Signature preview could not be loaded." : emptyMessage}
          </Text>
        )}
      </Flex>

      <FormControl mb={4}>
        <FormLabel>Source</FormLabel>
        <RadioGroup value={mode} onChange={handleModeChange}>
          <HStack spacing={6}>
            <Radio value="upload">Upload Image</Radio>
            <Radio value="url">Image URL</Radio>
          </HStack>
        </RadioGroup>
      </FormControl>

      {mode === "upload" ? (
        <FormControl mb={4} isInvalid={Boolean(error)}>
          <FormLabel>Signature Image</FormLabel>
          <Button
            as="label"
            leftIcon={<AttachmentIcon />}
            size="sm"
            variant="outline"
            cursor="pointer"
          >
            Choose Image
            <Input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              display="none"
              onChange={handleFileChange}
            />
          </Button>
          {file && (
            <Text color={mutedColor} fontSize="sm" mt={2}>
              {file.name}
            </Text>
          )}
          <FormErrorMessage>{error}</FormErrorMessage>
        </FormControl>
      ) : (
        <FormControl mb={4} isInvalid={Boolean(error)}>
          <FormLabel>Signature URL</FormLabel>
          <Input
            value={url}
            onChange={handleUrlChange}
            placeholder="https://example.com/signature.png"
          />
          <FormErrorMessage>{error}</FormErrorMessage>
        </FormControl>
      )}

      <HStack spacing={3}>
        <Button
          colorScheme="orange"
          onClick={handleSave}
          isDisabled={!canSave || isSaving}
          leftIcon={isSaving ? <Spinner size="xs" /> : undefined}
        >
          Save Signature
        </Button>
        <Button
          colorScheme="red"
          variant="outline"
          onClick={onOpen}
          isDisabled={!hasSignature || isDeleting}
        >
          Remove Signature
        </Button>
      </HStack>

      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Remove digital signature?
            </AlertDialogHeader>
            <AlertDialogBody>
              This signature will no longer be available for future documents
              or approvals.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleDelete}
                ml={3}
                isLoading={isDeleting}
              >
                Remove
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default UserDigitalSignatureManager;
